import type { ComponentType, ReactNode } from "react";
import { AlertOctagon, Clock, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CredentialCompleteness } from "../types";

type AttentionStat = {
  count: number;
  label: string;
  icon: ComponentType<{ className?: string }>;
  className: string;
};

/** Only the counts that need action are shown, so a provider with nothing
 * outstanding gets a single clean line instead of a row of zeroes. */
function attentionStats(completeness: CredentialCompleteness): AttentionStat[] {
  const stats: AttentionStat[] = [
    {
      count: completeness.expiredCount,
      label: "expired",
      icon: AlertOctagon,
      className: "text-red-700",
    },
    {
      /**
       * The REQUIRED gaps, not every gap.
       *
       * `completeness.missingCount` counts each optional credential nobody has
       * supplied as well, which is most of the catalogue. Derive the required
       * gaps from the required totals to agree with the roster's missing count.
       *
       * Optional gaps are not lost: every one of them still renders as a
       * "Missing / Optional" card in its section, where it is a fact about that
       * credential rather than a debt in a headline.
       */
      count: Math.max(
        0,
        completeness.requiredTotal - completeness.requiredComplete,
      ),
      label: "missing",
      icon: HelpCircle,
      className: "text-red-700",
    },
  ];
  return stats.filter((stat) => stat.count > 0);
}

/** Compact summary of expired and missing credentials with optional actions. */
export function CompletenessMeter({
  completeness,
  actions,
  appearance = "default",
}: {
  completeness: CredentialCompleteness;
  actions?: ReactNode;
  appearance?: "default" | "panel";
}) {
  const stats = attentionStats(completeness);
  const isPanel = appearance === "panel";
  if (isPanel && completeness.expiringCount > 0) {
    stats.push({
      count: completeness.expiringCount,
      label: "expiring soon",
      icon: Clock,
      className: "text-amber-800",
    });
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-xs sm:p-5",
        isPanel && "block space-y-2 p-3 sm:p-4",
      )}
    >
      {isPanel && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-900">
              Credential overview
            </h3>
            {actions && <div className="shrink-0">{actions}</div>}
          </div>
        </div>
      )}
      <div
        className={cn(
          "flex flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-sm",
          isPanel && "gap-x-4 gap-y-2 text-xs font-medium",
        )}
      >
        {stats.map((stat) => (
          <span
            key={stat.label}
            className={cn(
              "inline-flex items-center gap-1.5 whitespace-nowrap",
              stat.className,
            )}
          >
            <stat.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {stat.count} {stat.label}
          </span>
        ))}
        {stats.length === 0 && (
          <span className="text-emerald-700">
            {isPanel
              ? "No credentials need attention"
              : "No expired or missing credentials"}
          </span>
        )}
      </div>
      {!isPanel && actions && <div className="ml-auto shrink-0">{actions}</div>}
    </div>
  );
}
